import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import WorkflowIndicator from '../components/WorkflowIndicator';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const ZOBOX_PRIMARY = '#2b3380';

function PurchaseManagement({ user, onLogout }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'pr');
  const [loading, setLoading] = useState(false);

  // Data states
  const [prs, setPrs] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [pos, setPos] = useState([]);
  const [grns, setGrns] = useState([]);
  const [modalType, setModalType] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [formData, setFormData] = useState({ product_name: '', quantity: 1, department: '', justification: '', supplier_name: '', unit_price: '', delivery_time: '7 days', payment_terms: 'Credit', expected_delivery: '', po_number: '' });
  const [formError, setFormError] = useState('');

  // Workflow steps
  const purchaseWorkflowSteps = [
    { id: 'product', label: 'Product' },
    { id: 'pr', label: 'Purchase Requisition' },
    { id: 'quotation', label: 'Quotation' },
    { id: 'po', label: 'Purchase Order' },
    { id: 'grn', label: 'GRN' },
    { id: 'inventory', label: 'Inventory' }
  ];

  const tabs = [
    { id: 'pr', name: 'Purchase Requisitions', icon: '📝' },
    { id: 'quotations', name: 'Quotations', icon: '💰' },
    { id: 'po', name: 'Purchase Orders', icon: '📄' },
    { id: 'grn', name: 'GRN', icon: '📦' }
  ];

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  useEffect(() => {
    if (activeTab === 'pr') {
      fetchPRs();
    } else if (activeTab === 'quotations') {
      fetchQuotations();
    } else if (activeTab === 'po') {
      fetchPOs();
    } else if (activeTab === 'grn') {
      fetchGRNs();
    }
  }, [activeTab]);

  const fetchPRs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/purchase-requisitions`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setPrs(response.data.prs || []);
    } catch (error) {
      console.error('Error fetching PRs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuotations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/quotations`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setQuotations(response.data.quotations || []);
    } catch (error) {
      console.error('Error fetching quotations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPOs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/purchase-orders`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setPos(response.data.pos || []);
    } catch (error) {
      console.error('Error fetching POs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGRNs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/grns`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setGrns(response.data.grns || []);
    } catch (error) {
      console.error('Error fetching GRNs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeColor = (status) => {
    const statusColors = {
      'Draft': 'bg-gray-100 text-gray-800',
      'Pending Approval': 'bg-yellow-100 text-yellow-800',
      'Approved': 'bg-green-100 text-green-800',
      'Rejected': 'bg-red-100 text-red-800',
      'Sent': 'bg-blue-100 text-blue-800',
      'Confirmed': 'bg-green-100 text-green-800',
      'Partially Received': 'bg-orange-100 text-orange-800',
      'Fully Received': 'bg-green-100 text-green-800',
      'Verified': 'bg-green-100 text-green-800',
      'Posted to Inventory': 'bg-purple-100 text-purple-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  const openCreateModal = (type) => {
    setSelectedRecord(null);
    setFormError('');
    setFormData({ product_name: '', quantity: 1, department: '', justification: '', supplier_name: '', unit_price: '', delivery_time: '7 days', payment_terms: 'Credit', expected_delivery: '', po_number: '' });
    setModalType(type);
  };

  const submitRecord = async (event) => {
    event.preventDefault();
    const endpoint = { pr: 'purchase-requisitions', quotation: 'quotations', po: 'purchase-orders', grn: 'grns' }[modalType];
    try {
      setLoading(true);
      await axios.post(`${API}/api/${endpoint}`, formData, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      setModalType(null);
      if (modalType === 'pr') await fetchPRs();
      if (modalType === 'quotation') await fetchQuotations();
      if (modalType === 'po') await fetchPOs();
      if (modalType === 'grn') await fetchGRNs();
    } catch (error) {
      setFormError(error.response?.data?.detail || 'Unable to save this record');
    } finally {
      setLoading(false);
    }
  };

  const modalTitle = { pr: 'Create Purchase Requisition', quotation: 'Create Request for Quotation', po: 'Create Purchase Order', grn: 'Create Goods Receipt Note' }[modalType];

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Purchase Management</h1>
          <p className="mt-2 text-gray-600">Complete procurement workflow from requisition to inventory</p>
        </div>

        {/* Workflow Indicator */}
        <WorkflowIndicator 
          steps={purchaseWorkflowSteps} 
          currentStep={activeTab} 
        />

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'border-transparent text-gray-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                style={activeTab === tab.id ? { borderColor: ZOBOX_PRIMARY, color: ZOBOX_PRIMARY } : {}}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow">
          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: ZOBOX_PRIMARY }}></div>
              <p className="mt-4 text-gray-600">Loading...</p>
            </div>
          ) : (
            <>
              {/* Purchase Requisitions Tab */}
              {activeTab === 'pr' && (
                <div className="p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-semibold text-gray-900">Purchase Requisitions</h2>
                    <button 
                      onClick={() => openCreateModal('pr')}
                      className="px-4 py-2 text-white rounded-lg font-medium hover:opacity-90"
                      style={{ backgroundColor: ZOBOX_PRIMARY }}
                    >
                      + Create PR
                    </button>
                  </div>

                  {prs.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="text-6xl mb-4">📝</div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">No Purchase Requisitions</h3>
                      <p className="text-gray-600 mb-4">Start by creating your first purchase requisition</p>
                      <button 
                        onClick={() => openCreateModal('pr')}
                        className="px-4 py-2 text-white rounded-lg font-medium"
                        style={{ backgroundColor: ZOBOX_PRIMARY }}
                      >
                        Create First PR
                      </button>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">PR Number</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Requested By</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Items</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {prs.map((pr) => (
                            <tr key={pr.pr_id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" style={{ color: ZOBOX_PRIMARY }}>{pr.pr_number}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{pr.requested_by}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{pr.items?.length || 0} items</td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadgeColor(pr.status)}`}>
                                  {pr.status}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                {new Date(pr.created_at).toLocaleDateString()}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                <button onClick={() => setSelectedRecord(pr)} className="text-gray-600 hover:text-gray-900">View</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Quotations Tab */}
              {activeTab === 'quotations' && (
                <div className="p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-semibold text-gray-900">Quotations</h2>
                    <button 
                      onClick={() => openCreateModal('quotation')}
                      className="px-4 py-2 text-white rounded-lg font-medium hover:opacity-90"
                      style={{ backgroundColor: ZOBOX_PRIMARY }}
                    >
                      + Create RFQ
                    </button>
                  </div>

                  {quotations.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="text-6xl mb-4">💰</div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">No Quotations</h3>
                      <p className="text-gray-600 mb-4">Request quotes from suppliers to compare prices</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {quotations.map((quote) => (
                        <button type="button" onClick={() => setSelectedRecord(quote)} key={quote.quotation_id} className="border rounded-lg p-4 hover:shadow-lg transition text-left w-full">
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <h3 className="font-semibold text-gray-900">{quote.quotation_number}</h3>
                              <p className="text-sm text-gray-600">{quote.supplier_name}</p>
                            </div>
                            <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadgeColor(quote.status)}`}>
                              {quote.status}
                            </span>
                          </div>
                          <div className="text-2xl font-bold mb-2" style={{ color: ZOBOX_PRIMARY }}>
                            ₹{quote.total_amount?.toLocaleString() || 0}
                          </div>
                          <div className="text-sm text-gray-600">
                            {quote.items?.length || 0} items • {quote.delivery_time || 'N/A'} delivery
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Purchase Orders Tab */}
              {activeTab === 'po' && (
                <div className="p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-semibold text-gray-900">Purchase Orders</h2>
                    <button 
                      onClick={() => openCreateModal('po')}
                      className="px-4 py-2 text-white rounded-lg font-medium hover:opacity-90"
                      style={{ backgroundColor: ZOBOX_PRIMARY }}
                    >
                      + Create PO
                    </button>
                  </div>

                  {pos.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="text-6xl mb-4">📄</div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">No Purchase Orders</h3>
                      <p className="text-gray-600 mb-4">Create purchase orders from approved quotations</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {pos.map((po) => (
                        <button type="button" onClick={() => setSelectedRecord(po)} key={po.po_id} className="border rounded-lg p-6 hover:shadow-lg transition text-left w-full">
                          <div className="flex justify-between items-start">
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900">{po.po_number}</h3>
                              <p className="text-sm text-gray-600 mt-1">{po.supplier_name}</p>
                              <p className="text-sm text-gray-500 mt-1">{po.items?.length || 0} items</p>
                            </div>
                            <div className="text-right">
                              <div className="text-2xl font-bold mb-2" style={{ color: ZOBOX_PRIMARY }}>
                                ₹{po.total_amount?.toLocaleString() || 0}
                              </div>
                              <span className={`px-3 py-1 text-xs rounded-full ${getStatusBadgeColor(po.status)}`}>
                                {po.status}
                              </span>
                            </div>
                          </div>
                          <div className="mt-4 flex gap-4 text-sm text-gray-600">
                            <span>Payment: {po.payment_terms}</span>
                            <span>•</span>
                            <span>Expected: {po.expected_delivery ? new Date(po.expected_delivery).toLocaleDateString() : 'TBD'}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* GRN Tab */}
              {activeTab === 'grn' && (
                <div className="p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-semibold text-gray-900">Goods Receipt Notes</h2>
                    <button 
                      onClick={() => openCreateModal('grn')}
                      className="px-4 py-2 text-white rounded-lg font-medium hover:opacity-90"
                      style={{ backgroundColor: ZOBOX_PRIMARY }}
                    >
                      + Create GRN
                    </button>
                  </div>

                  {grns.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="text-6xl mb-4">📦</div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">No GRNs</h3>
                      <p className="text-gray-600 mb-4">Record goods received against purchase orders</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">GRN Number</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">PO Number</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Items</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Received Date</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {grns.map((grn) => (
                            <tr key={grn.grn_id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" style={{ color: ZOBOX_PRIMARY }}>{grn.grn_number}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{grn.po_number}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{grn.supplier_name}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{grn.items?.length || 0} items</td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadgeColor(grn.status)}`}>
                                  {grn.status}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                {new Date(grn.received_date).toLocaleDateString()}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                <button onClick={() => setSelectedRecord(grn)} className="text-gray-600 hover:text-gray-900">View</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {modalType && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true">
            <form onSubmit={submitRecord} className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-5">
                <h2 className="text-xl font-bold">{modalTitle}</h2>
                <button type="button" onClick={() => setModalType(null)} className="text-2xl text-gray-500" aria-label="Close">×</button>
              </div>
              {formError && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg">{formError}</div>}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="text-sm font-medium">Product / Item
                  <input required value={formData.product_name} onChange={e => setFormData({...formData, product_name: e.target.value})} className="mt-1 w-full border rounded-lg px-3 py-2" />
                </label>
                <label className="text-sm font-medium">Quantity
                  <input required min="1" type="number" value={formData.quantity} onChange={e => setFormData({...formData, quantity: e.target.value})} className="mt-1 w-full border rounded-lg px-3 py-2" />
                </label>
                {modalType === 'pr' && <>
                  <label className="text-sm font-medium">Department<input value={formData.department} onChange={e => setFormData({...formData, department: e.target.value})} className="mt-1 w-full border rounded-lg px-3 py-2" /></label>
                  <label className="text-sm font-medium">Justification<input value={formData.justification} onChange={e => setFormData({...formData, justification: e.target.value})} className="mt-1 w-full border rounded-lg px-3 py-2" /></label>
                </>}
                {['quotation', 'po', 'grn'].includes(modalType) && <label className="text-sm font-medium">Supplier Name<input required value={formData.supplier_name} onChange={e => setFormData({...formData, supplier_name: e.target.value})} className="mt-1 w-full border rounded-lg px-3 py-2" /></label>}
                {['quotation', 'po'].includes(modalType) && <label className="text-sm font-medium">Unit Price<input required min="0" type="number" value={formData.unit_price} onChange={e => setFormData({...formData, unit_price: e.target.value})} className="mt-1 w-full border rounded-lg px-3 py-2" /></label>}
                {modalType === 'quotation' && <label className="text-sm font-medium">Delivery Time<input value={formData.delivery_time} onChange={e => setFormData({...formData, delivery_time: e.target.value})} className="mt-1 w-full border rounded-lg px-3 py-2" /></label>}
                {modalType === 'po' && <>
                  <label className="text-sm font-medium">Payment Terms<select value={formData.payment_terms} onChange={e => setFormData({...formData, payment_terms: e.target.value})} className="mt-1 w-full border rounded-lg px-3 py-2"><option>Credit</option><option>Advance</option><option>COD</option></select></label>
                  <label className="text-sm font-medium">Expected Delivery<input type="date" value={formData.expected_delivery} onChange={e => setFormData({...formData, expected_delivery: e.target.value})} className="mt-1 w-full border rounded-lg px-3 py-2" /></label>
                </>}
                {modalType === 'grn' && <label className="text-sm font-medium">PO Number<input value={formData.po_number} onChange={e => setFormData({...formData, po_number: e.target.value})} className="mt-1 w-full border rounded-lg px-3 py-2" /></label>}
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button type="button" onClick={() => setModalType(null)} className="px-4 py-2 border rounded-lg">Cancel</button>
                <button type="submit" className="px-5 py-2 text-white rounded-lg" style={{ backgroundColor: ZOBOX_PRIMARY }}>Save</button>
              </div>
            </form>
          </div>
        )}

        {selectedRecord && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-5"><h2 className="text-xl font-bold">Record Details</h2><button onClick={() => setSelectedRecord(null)} className="text-2xl text-gray-500" aria-label="Close">×</button></div>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(selectedRecord).filter(([, value]) => !Array.isArray(value) && value !== null && typeof value !== 'object').map(([key, value]) => (
                  <div key={key} className="bg-gray-50 rounded-lg p-3"><dt className="text-xs uppercase text-gray-500">{key.replace(/_/g, ' ')}</dt><dd className="font-medium break-words">{String(value)}</dd></div>
                ))}
                <div className="bg-gray-50 rounded-lg p-3"><dt className="text-xs uppercase text-gray-500">Items</dt><dd className="font-medium">{selectedRecord.items?.length || 0}</dd></div>
              </dl>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default PurchaseManagement;
